/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXSelector, useStateXValue } from '@cloudio-saas/statex';
import { FormControl, FormHelperText } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { getManager, PropertyChooserProp } from 'componentregistry';
import { VirtualizedSelectInput } from 'custom/SelectInput';
import { designPath } from 'designer/atoms';
import { helpTextSelector } from 'designer/state';
import React, { memo } from 'react';
import { isEmpty } from 'utils/isEmpty';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
});

function PropertyChooserProperty({
  activeItemId,
  def,
  appUid,
  pageId,
  property,
  mgr,
  value = getDefaultValues<string>(mgr, property),
  onChange,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<PropertyChooserProp, string>) {
  const classes = useStyles();
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const handleChange = (val: string) => {
    if (val !== value) {
      onChange(property, val);
    }
  };

  const targetItemId = useStateXValue(
    designPath({
      appUid,
      pageId,
      itemId: activeItemId,
      key: 'props',
      subkey: def.itemIdProp,
    }),
    '',
  );

  if (isEmpty(targetItemId)) {
    return null;
  }
  const item = platform.getItem(appUid, pageId, targetItemId);

  if (!item?.type) {
    return null;
  }

  const tmgr = getManager(item.type);

  const options = Object.keys(tmgr.props).map((key) => ({
    value: key,
    // @ts-ignore dynamic key
    label: tmgr.props[key].label,
  }));
  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  return (
    <FormControl
      data-testid={property}
      variant="standard"
      required={required}
      fullWidth
      component="fieldset"
      className={classes.root}
    >
      <VirtualizedSelectInput
        disabled={isViewOnlyUser}
        value={value}
        options={options}
        onChange={handleChange}
        disableClearable
        label={def.label}
        variant="standard"
      />
      <FormHelperText>{helpText}</FormHelperText>
    </FormControl>
  );
}

export default memo(PropertyChooserProperty);
